import { useState } from "react";
import {
  Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ComposedChart, Area,
} from "recharts";
import { InfoTip } from "./InfoTip";
import type { HistoryPoint } from "../hooks/useHistoryData";

interface HistoryChartProps {
  history: HistoryPoint[];
}

type MetricId = "avg_hf" | "near_liq" | "supplied" | "lst_shortfall" | "ethena_shortfall";

const METRICS: { id: MetricId; label: string; tip: string; color: string; format: (v: number) => string }[] = [
  {
    id: "avg_hf",
    label: "Avg HF",
    tip: "Collateral-weighted average health factor across all positions over time.",
    color: "#22c55e",
    format: (v) => v.toFixed(2),
  },
  {
    id: "near_liq",
    label: "Near Liquidation",
    tip: "Number of positions with health factor below 1.1 over time.",
    color: "#eab308",
    format: (v) => v.toLocaleString(),
  },
  {
    id: "supplied",
    label: "Total Supplied",
    tip: "Total value supplied across all protocols over time.",
    color: "#6366f1",
    format: (v) => `$${(v / 1e9).toFixed(1)}B`,
  },
  {
    id: "lst_shortfall",
    label: "LST Depeg Shortfall",
    tip: "Liquidity shortfall at -5% LST depeg over time. Higher = more risk.",
    color: "#ef4444",
    format: (v) => `$${(v / 1e9).toFixed(2)}B`,
  },
  {
    id: "ethena_shortfall",
    label: "Ethena Shortfall",
    tip: "Liquidity shortfall at -5% Ethena depeg over time.",
    color: "#f97316",
    format: (v) => `$${(v / 1e6).toFixed(0)}M`,
  },
];

export function HistoryChart({ history }: HistoryChartProps) {
  const [activeMetric, setActiveMetric] = useState<MetricId>("lst_shortfall");

  if (history.length < 2) return null;

  const metric = METRICS.find((m) => m.id === activeMetric)!;

  const chartData = history.map((p) => {
    let value = 0;
    if (activeMetric === "avg_hf") value = p.avg_hf;
    else if (activeMetric === "near_liq") value = p.near_liq;
    else if (activeMetric === "supplied") value = p.supplied;
    else if (activeMetric === "lst_shortfall") value = p.lst_depeg_short ?? 0;
    else if (activeMetric === "ethena_shortfall") value = p.ethena_depeg_short ?? 0;

    return {
      time: new Date(p.t).getTime(),
      value,
    };
  });

  // Time range
  const firstTime = new Date(history[0].t);
  const lastTime = new Date(history[history.length - 1].t);
  const rangeHours = (lastTime.getTime() - firstTime.getTime()) / 3600000;

  return (
    <div className="card">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-base font-semibold">
            <InfoTip text="Historical tracking of key risk metrics. Data is collected every 15 minutes and stored for 30 days.">
              Historical Trends
            </InfoTip>
          </h2>
          <p className="text-xs text-text-muted mt-0.5">
            {history.length} data points over {rangeHours < 48 ? `${rangeHours.toFixed(0)}h` : `${(rangeHours / 24).toFixed(1)} days`}
          </p>
        </div>
      </div>

      {/* Metric selector */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {METRICS.map((m) => (
          <button
            key={m.id}
            onClick={() => setActiveMetric(m.id)}
            className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
              activeMetric === m.id
                ? "bg-accent text-white"
                : "bg-surface-700 text-text-secondary hover:text-text-primary hover:bg-surface-600"
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={220}>
        <ComposedChart data={chartData} margin={{ top: 8, right: 12, bottom: 4, left: -4 }}>
          <defs>
            <linearGradient id="histGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={metric.color} stopOpacity={0.2} />
              <stop offset="100%" stopColor={metric.color} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="time"
            type="number"
            domain={["dataMin", "dataMax"]}
            tickFormatter={(t: number) => {
              const d = new Date(t);
              return rangeHours < 48
                ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                : d.toLocaleDateString([], { month: "short", day: "numeric" });
            }}
            tick={{ fontSize: 10, fill: "#64748b" }}
            axisLine={{ stroke: "#282a3a" }}
            tickLine={false}
          />
          <YAxis
            tickFormatter={(v: number) => metric.format(v)}
            tick={{ fontSize: 10, fill: "#64748b" }}
            axisLine={false}
            tickLine={false}
            width={65}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "#1e2030",
              border: "1px solid #282a3a",
              borderRadius: "6px",
              fontSize: "11px",
            }}
            labelFormatter={(t: number) => new Date(t).toLocaleString()}
            formatter={(value: number) => [metric.format(value), metric.label]}
          />
          <Area
            type="monotone"
            dataKey="value"
            fill="url(#histGrad)"
            stroke="none"
          />
          <Line
            type="monotone"
            dataKey="value"
            stroke={metric.color}
            strokeWidth={1.5}
            dot={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
